/**
 * Pause avant emission, pour derouler les tests de conformite INSi qui exigent une
 * MANIPULATION ou une ATTENTE entre la preparation de la requete et son envoi.
 *
 * Trois tests du guide en ont besoin, et le parcours normal les rend infaisables parce qu'il
 * enchaine lecture de carte et emission en une seconde :
 *
 *  - **1.03** : selectionner un beneficiaire sur la carte INSi 01, REMPLACER la carte par
 *    l'INSi 04, puis envoyer. Le LPS doit alors refuser l'envoi ;
 *  - **1.02 cas 1** : une recherche, 20 minutes d'attente, une seconde recherche ;
 *  - **2.01 cas 1** : une recherche par traits, UNE HEURE d'attente, une seconde recherche.
 *
 * Outil de test, jamais de production : la pause n'est proposee que hors build de production
 * (`NODE_ENV`), et reste desactivee jusqu'a ce qu'on la coche explicitement dans la modale
 * d'identite. L'etat et l'horodatage du dernier appel vivent dans `localStorage`, pour
 * survivre au rechargement de page pendant l'heure d'attente du test 2.01.
 */

const ENABLED_KEY = 'ameli.insi.conformityPause.enabled'
const LAST_CALL_KEY = 'ameli.insi.conformityPause.lastCallAt'

/** `localStorage` peut lever (SSR, fenetre privee, cookies bloques) : jamais sans garde. */
const storage = (): Storage | null => {
  try {
    return typeof window === 'undefined' ? null : window.localStorage
  } catch {
    return null
  }
}

/** La pause est-elle proposable sur ce build ? Faux en production, sans condition. */
export const isConformityPauseAvailable = (): boolean => 'production' !== process.env.NODE_ENV

/** Etat courant de la pause. Toujours faux en production. */
export const isConformityPauseEnabled = (): boolean => {
  if (!isConformityPauseAvailable()) return false
  try {
    return '1' === storage()?.getItem(ENABLED_KEY)
  } catch {
    return false
  }
}

export const setConformityPauseEnabled = (enabled: boolean): void => {
  try {
    storage()?.setItem(ENABLED_KEY, enabled ? '1' : '0')
  } catch {
    // Preference de confort : si le stockage refuse, la pause vaut juste pour cette session.
  }
}

/** Horodate un appel INSi reellement emis (base du chrono « depuis le dernier appel »). */
export const markInsiCall = (at: number): void => {
  if (!isConformityPauseAvailable()) return
  try {
    storage()?.setItem(LAST_CALL_KEY, String(at))
  } catch {
    // idem
  }
}

/** Horodatage du dernier appel INSi emis, ou null si aucun n'est connu. */
export const getLastInsiCallAt = (): number | null => {
  try {
    const raw = storage()?.getItem(LAST_CALL_KEY)
    if (!raw) return null
    const value = Number(raw)
    return Number.isFinite(value) ? value : null
  } catch {
    return null
  }
}

/**
 * Duree lisible d'un ecart en millisecondes : `1 h 04 min 09 s`, `20 min 03 s`, `41 s`.
 * Les seuils des tests se lisent en minutes et en heures, l'unite la plus grande d'abord.
 */
export const formatElapsed = (ms: number): string => {
  const total = Math.max(0, Math.floor(ms / 1000))
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  const pad = (n: number) => String(n).padStart(2, '0')
  if (h > 0) return `${h} h ${pad(m)} min ${pad(s)} s`
  if (m > 0) return `${m} min ${pad(s)} s`
  return `${s} s`
}

/**
 * Fixtures d'injection livrees par le CNDA, deposees dans
 * `pharmedigroup-api/var/ameli-test/insi/`. Le nom transmis dans `X-Ameli-Test` est celui du
 * fichier SANS son extension.
 *
 * Le service d'injection lit `{AMELI_TEST_INJECTION_DIR}/{service}/{fixture}.xml` : les
 * fichiers doivent donc bien etre dans le sous-dossier `insi/`, pas a la racine.
 */
export const INSI_FIXTURES = [
  'REP_CR01',
  'REP_CR02',
  'TEST_1.10',
  'TEST_2.04_cas2',
  'TEST_2.05',
  'TEST_2.08_cas1',
  'TEST_2.08_cas2',
  'TEST_3.05_cas_3',
  'TEST_NIA',
] as const

/** Levee quand l'utilisateur annule depuis la pause : la sequence d'appels s'arrete net. */
export class ConformityAbortError extends Error {
  constructor() {
    super('Sequence interrompue depuis la pause de conformite.')
    this.name = 'ConformityAbortError'
  }
}

/** Ce que la pause sait de l'appel qu'elle retient. */
export interface BeforeCallInfo {
  /** Rang de l'appel dans la sequence, a partir de 1. */
  index: number
  /** Nombre d'appels prevus, si connu d'avance. */
  total?: number
  /** Ce qui distingue cet appel des autres : nom utilise, prenom(s), lieu de naissance. */
  label: string
  /** N° de serie logique de la carte lue (WS_INS1 seulement). */
  serial?: string
  /** Instant de la lecture de la carte (WS_INS1 seulement). */
  readAt?: number
}

/**
 * Retenue avant emission. Appelee par les hooks metier avant CHAQUE appel au teleservice.
 * Resout avec la fixture a injecter pour cet appel (aucune si non renseignee), ou rejette
 * avec `ConformityAbortError` si l'utilisateur annule.
 */
export type BeforeCallGate = (info: BeforeCallInfo) => Promise<{ testFixture?: string }>
